import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { formatDateTimeCompact } from "@/utils/dateUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeletionStatusBadge } from "@/components/common/data-deletion/DeletionStatusBadge";
import { DeletionJobStatusDialog } from "@/components/common/data-deletion/DeletionJobStatusDialog";
import { GuestDeletionDialog } from "@/components/common/data-deletion/GuestDeletionDialog";
import { useDeletionJobs } from "@/hooks/useDataDeletion";
import {
  DeletionJob,
  DeletionJobStatus,
  DeletionJobsQuery,
  DeletionTargetType,
} from "@/types/data-deletion";

const PAGE_LIMIT = 20;
const ALL = "ALL";

const STATUS_OPTIONS = Object.values(DeletionJobStatus);
const TARGET_TYPE_OPTIONS = Object.values(DeletionTargetType);

export const DeletionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<DeletionJobStatus | "ALL">(
    ALL,
  );
  const [targetTypeFilter, setTargetTypeFilter] = useState<
    DeletionTargetType | "ALL"
  >(ALL);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);

  const query = useMemo<DeletionJobsQuery>(
    () => ({
      page,
      limit: PAGE_LIMIT,
      ...(statusFilter !== ALL ? { status: statusFilter } : {}),
      ...(targetTypeFilter !== ALL ? { targetType: targetTypeFilter } : {}),
    }),
    [page, statusFilter, targetTypeFilter],
  );

  const { data, isLoading } = useDeletionJobs(query);
  const jobs = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_LIMIT));

  const openJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setStatusDialogOpen(true);
  };

  const columns: Column<DeletionJob>[] = [
    {
      key: "createdAt",
      header: t("dataDeletion.audit.columns.created"),
      render: (value: string) => formatDateTimeCompact(value),
    },
    {
      key: "targetType",
      header: t("dataDeletion.audit.columns.target"),
      render: (_: unknown, row: DeletionJob) => (
        <div className="flex flex-col items-start gap-1">
          <span className="font-medium">
            {t(`dataDeletion.targetType.${row.targetType}`)}
          </span>
          {row.targetLabel ? (
            <span className="break-all text-xs text-muted-foreground">
              {row.targetLabel}
            </span>
          ) : null}
          {row.targetType === DeletionTargetType.GUEST ? (
            <Badge variant="new_secondary">
              {t(`dataDeletion.mode.${row.mode}`)}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: "targetId",
      header: t("dataDeletion.audit.columns.targetId"),
      render: (value: string) => (
        <span className="break-all font-mono text-xs text-muted-foreground">
          {value}
        </span>
      ),
    },
    {
      key: "status",
      header: t("dataDeletion.audit.columns.status"),
      render: (_: unknown, row: DeletionJob) => (
        <DeletionStatusBadge status={row.status} />
      ),
    },
    {
      key: "requestedByEmail",
      header: t("dataDeletion.audit.columns.requestedBy"),
      render: (value: string | null) => value ?? "—",
    },
    {
      key: "completedAt",
      header: t("dataDeletion.audit.columns.completed"),
      render: (value: string | null) => formatDateTimeCompact(value),
    },
  ];

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("dataDeletion.audit.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("dataDeletion.audit.subtitle")}
          </p>
        </div>
        <Button
          type="button"
          variant="new_destructive"
          className="w-fit shrink-0"
          onClick={() => setGuestDialogOpen(true)}
        >
          <Trash2 className="size-4" />
          {t("dataDeletion.audit.eraseGuest")}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-2xl border bg-background p-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-none">
            {t("dataDeletion.audit.filters.status")}
          </span>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value as DeletionJobStatus | "ALL");
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t("dataDeletion.audit.filters.all")}
              </SelectItem>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`dataDeletion.status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-none">
            {t("dataDeletion.audit.filters.targetType")}
          </span>
          <Select
            value={targetTypeFilter}
            onValueChange={(value) => {
              setTargetTypeFilter(value as DeletionTargetType | "ALL");
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t("dataDeletion.audit.filters.all")}
              </SelectItem>
              {TARGET_TYPE_OPTIONS.map((targetType) => (
                <SelectItem key={targetType} value={targetType}>
                  {t(`dataDeletion.targetType.${targetType}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border bg-background p-6">
        <DataTable
          data={jobs}
          columns={columns}
          loading={isLoading}
          getRowKey={(row) => row.id}
          onRowClick={(row) => openJob(row.id)}
          pagination={{
            currentPage: page,
            totalPages,
            onPageChange: setPage,
          }}
          emptyMessage={t("dataDeletion.audit.empty")}
        />
      </div>

      <DeletionJobStatusDialog
        jobId={selectedJobId}
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
      />

      <GuestDeletionDialog
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
      />
    </div>
  );
};
